import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useTokenStore from '../../stores/tokens';
import { compatApi } from '../../utils/Intercepter';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { renderMarkdown, MD_STYLES } from '../../utils/markdownUtils';

const SCOPES = [
    { value:'private', label:'개인',    color:'#8b8a99', bg:'#f4f3f9' },
    { value:'org',     label:'조직 공유', color:PURPLE,   bg:hexToRgba(PURPLE,.12) },
    { value:'all',     label:'전체 공유', color:'#5BC2B2', bg:'rgba(91,194,178,.14)' },
];
function scopeMeta(v) { return SCOPES.find(s => s.value === v) || SCOPES[0]; }


export default function NoteViewPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useTokenStore();
    const myEmail = user?.userEmail;
    const [note, setNote] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        compatApi.get(`/note/${id}`)
            .then(res => setNote(res.data))
            .catch(() => { toast.error('노트를 불러올 수 없습니다.'); navigate('/note'); })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    const handleDelete = async () => {
        if (!window.confirm('이 노트를 삭제할까요?')) return;
        await compatApi.del(`/note/${id}`);
        toast.success('삭제되었습니다.');
        navigate('/note');
    };

    if (loading) return <div style={{ minHeight:'calc(100vh - 61px)', background:'#f5f4fa', display:'flex', alignItems:'center', justifyContent:'center', color:'#8b8a99' }}>불러오는 중...</div>;
    if (!note) return null;

    const isMine = note.authorEmail === myEmail;
    const sc = scopeMeta(note.scope);

    return (
        <div style={{ minHeight:'calc(100vh - 61px)', background:'#f5f4fa' }}>
            <style>{MD_STYLES(PURPLE, MINT)}</style>
            <main style={{ maxWidth:920, margin:'0 auto', padding:'32px clamp(20px,3vw,40px) 64px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                    <button onClick={() => navigate('/note')} style={{ background:'transparent', border:'none', padding:'8px 0', color:'#6a6878', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                        노트 목록
                    </button>
                    {isMine && (
                        <div style={{ display:'flex', gap:8 }}>
                            <button onClick={() => navigate(`/note/${id}/edit`)} style={{ height:34, padding:'0 14px', borderRadius:8, background:'white', border:'1px solid #ececf2', color:'#3a3849', fontSize:12.5, fontWeight:500, cursor:'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:5 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>
                                수정
                            </button>
                            <button onClick={handleDelete} style={{ height:34, padding:'0 14px', borderRadius:8, background:'white', border:'1px solid #ececf2', color:'#c44569', fontSize:12.5, fontWeight:500, cursor:'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:5 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"/></svg>
                                삭제
                            </button>
                        </div>
                    )}
                </div>

                <article style={{ background:'white', borderRadius:16, border:'1px solid #f0eff5', boxShadow:'0 1px 2px rgba(60,50,120,.04), 0 8px 28px -16px rgba(60,50,120,.18)', padding:'36px 44px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11.5, fontWeight:600, color:sc.color, background:sc.bg, padding:'4px 10px', borderRadius:999 }}>{sc.label}</span>
                        <span style={{ fontSize:12, color:'#8b8a99' }}>
                            {note.updatedAt ? format(new Date(note.updatedAt), 'yyyy.MM.dd HH:mm') : ''} 수정
                            {note.createdAt !== note.updatedAt && <span style={{ marginLeft:8, color:'#aaa9b7' }}>· 작성 {note.createdAt ? format(new Date(note.createdAt), 'yyyy.MM.dd') : ''}</span>}
                        </span>
                    </div>

                    <h1 style={{ margin:0, fontSize:30, fontWeight:700, color:'#1f1d2e', letterSpacing:'-0.018em', lineHeight:1.3 }}>{note.title}</h1>

                    {note.tags?.length > 0 && (
                        <div style={{ display:'flex', flexWrap:'wrap', gap:5, margin:'12px 0 0' }}>
                            {note.tags.map(t => (
                                <span key={t} style={{ background:hexToRgba(PURPLE,.08), color:PURPLE, padding:'3px 10px', borderRadius:999, fontSize:12, fontWeight:500 }}>#{t}</span>
                            ))}
                        </div>
                    )}

                    <div style={{ display:'flex', alignItems:'center', gap:10, margin:'18px 0 26px', paddingBottom:22, borderBottom:'1px solid #f0eff5', flexWrap:'wrap' }}>
                        <div style={{ width:36, height:36, borderRadius:'50%', background:`linear-gradient(135deg,${PURPLE},${MINT})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12.5, fontWeight:600, color:'white', flexShrink:0 }}>{note.authorName?.slice(-2)}</div>
                        <div style={{ display:'flex', flexDirection:'column', lineHeight:1.3 }}>
                            <span style={{ fontSize:13.5, fontWeight:600, color:'#1f1d2e' }}>{note.authorName} <span style={{ color:'#8b8a99', fontWeight:400 }}>{note.authorRole}</span></span>
                            <span style={{ fontSize:11.5, color:'#8b8a99' }}>작성자</span>
                        </div>
                        {note.scope === 'org' && note.orgName && <>
                            <div style={{ width:1, height:28, background:'#ececf2', alignSelf:'center' }}/>
                            <div style={{ display:'flex', flexDirection:'column', lineHeight:1.3 }}>
                                <span style={{ fontSize:13.5, fontWeight:500, color:'#1f1d2e' }}>{note.orgName}</span>
                                <span style={{ fontSize:11.5, color:'#8b8a99' }}>조직</span>
                            </div>
                        </>}
                    </div>

                    <div className="md-body" dangerouslySetInnerHTML={{ __html: renderMarkdown(note.content) }}/>
                </article>
            </main>
        </div>
    );
}
